/**
 * MoonScene Engine Orchestrator
 * Integrates WebGLRenderer, Camera, MoonGlobe, Grid, Markers, Starfield,
 * and handles responsive resizing, raycasting, and performance telemetry.
 */

import * as THREE from 'three';
import { MoonGlobe } from './MoonGlobe.js';
import { OrbitManager } from './OrbitManager.js';
import { SelenographicGrid } from './SelenographicGrid.js';
import { MarkersManager } from './MarkersManager.js';
import { vector3ToLatLon, calculateAltitudeKm } from '../utils/math.js';

export class MoonScene {
  constructor(container) {
    this.container = container;
    this.width = container.clientWidth || window.innerWidth;
    this.height = container.clientHeight || window.innerHeight;

    this.sphereRadius = 5.0;
    this.autoRotate = false;
    this.autoRotateSpeed = 0.001;

    // Performance telemetry
    this.fps = 60;
    this.frameCount = 0;
    this.lastFpsUpdate = performance.now();
    this.hoverCoords = { lat: 0, lon: 0, valid: false };

    this.initRenderer();
    this.initScene();
    this.initEntities();
    this.initEventListeners();
    this.animate();
  }

  initRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.container.appendChild(this.renderer.domElement);
  }

  initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x020307);

    this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 1000);
    // Start viewing Nearside from an orbital distance of 14 units (~3,100 km altitude)
    this.camera.position.set(0, 0, 14);

    this.orbitManager = new OrbitManager(this.camera, this.renderer.domElement, this.sphereRadius);

    this.initStarfield();
  }

  initStarfield() {
    const starCount = 8000;
    const starGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(starCount * 3);
    const colors = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
      // Uniform distribution on deep celestial sphere
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      const r = 250 + Math.random() * 50;

      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      // Realistic stellar spectral classification (O, B, A, F, G, K, M)
      const temp = Math.random();
      if (temp > 0.85) {
        // Hot blue-white O/B stars
        colors[i * 3] = 0.75;
        colors[i * 3 + 1] = 0.85;
        colors[i * 3 + 2] = 1.0;
      } else if (temp > 0.4) {
        // Sun-like yellowish-white stars
        colors[i * 3] = 1.0;
        colors[i * 3 + 1] = 0.96;
        colors[i * 3 + 2] = 0.9;
      } else {
        // Red/orange M giants
        colors[i * 3] = 1.0;
        colors[i * 3 + 1] = 0.75;
        colors[i * 3 + 2] = 0.65;
      }
    }

    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const starMaterial = new THREE.PointsMaterial({
      size: 1.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      sizeAttenuation: true
    });

    this.starfield = new THREE.Points(starGeometry, starMaterial);
    this.scene.add(this.starfield);
  }

  initEntities() {
    const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.moonGlobe = new MoonGlobe(this.sphereRadius, maxAnisotropy);
    this.scene.add(this.moonGlobe.group);

    this.grid = new SelenographicGrid(this.sphereRadius * 1.002);
    this.scene.add(this.grid.group);

    this.markers = new MarkersManager(this.scene, this.camera, this.sphereRadius);

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
  }

  initEventListeners() {
    window.addEventListener('resize', () => this.onWindowResize());

    this.renderer.domElement.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.renderer.domElement.addEventListener('click', (e) => this.onClick(e));
  }

  onWindowResize() {
    this.width = this.container.clientWidth || window.innerWidth;
    this.height = this.container.clientHeight || window.innerHeight;

    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  }

  onMouseMove(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // 1. Check marker hover
    const hoveredFeature = this.markers.checkIntersection(this.mouse);
    if (hoveredFeature) {
      this.renderer.domElement.style.cursor = 'pointer';
      if (this.onFeatureHover) this.onFeatureHover(hoveredFeature, event.clientX, event.clientY);
      return;
    } else {
      this.renderer.domElement.style.cursor = 'grab';
      if (this.onFeatureHover) this.onFeatureHover(null);
    }

    // 2. Check raycast on Moon surface for live coordinates
    this.raycaster.setFromCamera(this.mouse, this.camera);
    if (this.moonGlobe && this.moonGlobe.mesh) {
      const intersects = this.raycaster.intersectObject(this.moonGlobe.mesh);
      if (intersects.length > 0) {
        const p = intersects[0].point;
        // Compensate for globe mesh Y-rotation of -PI/2
        const rotatedPoint = p.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        this.hoverCoords = { ...vector3ToLatLon(rotatedPoint, this.sphereRadius), valid: true };
      } else {
        this.hoverCoords.valid = false;
      }
    }
  }

  onClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const clickedFeature = this.markers.checkIntersection(this.mouse);
    if (clickedFeature && this.onFeatureClick) {
      this.onFeatureClick(clickedFeature);
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    const now = performance.now();
    this.frameCount++;
    if (now - this.lastFpsUpdate >= 500) {
      this.fps = Math.round((this.frameCount * 1000) / (now - this.lastFpsUpdate));
      this.frameCount = 0;
      this.lastFpsUpdate = now;
    }

    if (this.autoRotate && !this.orbitManager.isFlying) {
      this.moonGlobe.group.rotation.y += this.autoRotateSpeed;
      this.grid.group.rotation.y += this.autoRotateSpeed;
      this.markers.markersGroup.rotation.y += this.autoRotateSpeed;
    }

    this.markers.update(now * 0.001);
    this.orbitManager.update();

    this.renderer.render(this.scene, this.camera);

    if (this.onTelemetryUpdate) {
      const camDist = this.orbitManager.getDistance();
      const altitudeKm = calculateAltitudeKm(camDist, this.sphereRadius);
      this.onTelemetryUpdate({
        fps: this.fps,
        altitudeKm: Math.round(altitudeKm),
        cameraDistance: camDist.toFixed(2),
        hoverCoords: this.hoverCoords,
        drawCalls: this.renderer.info.render.calls,
        triangles: this.renderer.info.render.triangles
      });
    }
  }
}
